/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { cssMap, jsx } from '@compiled/react';
import { FormattedMessage } from 'react-intl';
import type { MessageDescriptor } from 'react-intl';

import { fg } from '@atlaskit/platform-feature-flags';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { ATLASSIAN_BRANDING } from '../../../../branding';
import atlassianLogo from '../assets/atlassian-logo.svg';

import { LanguageSwitcher } from './LanguageSwitcher';
import { messages } from './messages';

type FooterLinkItem = {
	label: MessageDescriptor;
	/**
	 * Destination URL for the link. Set to the literal string `'TODO'`
	 * for items whose final destination has not yet been decided; these
	 * placeholders will be replaced before the footer is wired up to
	 * real anchor `href`s.
	 */
	url: string;
};

const LEGAL_LINKS: FooterLinkItem[] = [
	{ label: messages.privacyPolicy, url: 'https://www.atlassian.com/legal/privacy-policy' },
	{ label: messages.terms, url: 'https://www.atlassian.com/legal/cloud-terms-of-service' },
	{ label: messages.impressum, url: 'https://www.atlassian.com/legal/impressum' },
];

const styles = cssMap({
	column: {
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'space-between',
		alignItems: 'stretch',
		alignSelf: 'stretch',
		flex: '1 0 0',
	},
	top: {
		display: 'flex',
		flexDirection: 'column',
		alignSelf: 'stretch',
		gap: token('space.300'),
	},
	logoRow: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		gap: token('space.100'),
	},
	copyright: {
		fontFamily: token('font.family.body'),
		fontWeight: token('font.weight.regular'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		fontSize: '12px',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: '36px',
		// Figma spec uses `Neutral/Light/400` (#B7B9BE) for muted text
		// on the dark `#18191A` footer surface. There is no semantic
		// ADS token for muted text on a custom dark surface, so the
		// hex is inlined to match the design exactly.
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		color: '#B7B9BE',
		margin: 0,
	},
	logoImage: {
		display: 'block',
		// Dimensions taken from the Figma `❖ Logo` instance
		// (123.42 × 20). The SVG bakes in the `#B7B9BE` muted text
		// colour and white bug, so no CSS colour overrides are needed.
		width: '124px',
		height: '20px',
	},
	legalLinks: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		alignSelf: 'stretch',
		margin: 0,
		padding: 0,
		listStyle: 'none',
	},
	legalLink: {
		font: token('font.body.small'),
		fontFamily: token('font.family.body'),
		fontWeight: token('font.weight.regular'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: '36px',
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		color: '#B7B9BE',
		textDecoration: 'none',
		textAlign: 'left',
	},
});

/**
 * Left-most column of the microsite footer. Hosts the Atlassian logo,
 * copyright line, social-media icon row, legal links, and the active
 * language indicator.
 *
 * Currently presentational - placeholders for outbound links are stubs
 * and will be wired up in a follow-up change.
 */
export const BrandColumn = (): JSX.Element => {
	return (
		<Box xcss={styles.column}>
			<Box xcss={styles.top}>
				<div css={styles.logoRow}>
					<img
						css={styles.logoImage}
						src={atlassianLogo}
						alt={ATLASSIAN_BRANDING}
						width="124"
						height="20"
					/>
				</div>
				{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
				<p css={styles.copyright}>
					<FormattedMessage {...messages.copyright} />
				</p>
				<ul css={styles.legalLinks}>
					{LEGAL_LINKS.map((item) => (
						<li key={item.label.id}>
							{/* eslint-disable-next-line @atlaskit/design-system/no-html-anchor */}
							<a css={styles.legalLink} href={item.url}>
								<FormattedMessage {...item.label} />
							</a>
						</li>
					))}
				</ul>
			</Box>
			{fg('twg-microsite-enable-i18n') && <LanguageSwitcher />}
		</Box>
	);
};
